import Decimal from 'decimal.js';
import { useClientState } from '../store/client/state';

export interface PriceOptions {
    inclVat?: boolean
    country?: string
    currency?: string
}

export const useMonetary = () => {
    const {
        $i18n
    } = useNuxtApp();

    const {
        settings
    } = storeToRefs(useClientState());

    const round = (value: number, places: number) => new Decimal(value)
        .toDecimalPlaces(places)
        .toNumber();

    const reduce = (array: Array<any>, times: number) => array.map(item => new Decimal(item.amount).toDecimalPlaces(2).times(times)) // The item amount to Decimal and rounding it.
        .reduce((sum, val) => sum.plus(val), new Decimal(0)) // Sums up the amounts.
        .toDecimalPlaces(2) // Rounding total.
        .toNumber(); // Returning the number.

    const calculate = (price: number, taxes: Array<any>, quantity: number, inclVat: boolean) => new Decimal(price) // Price to Decimal.
        .toDecimalPlaces(2) // Roundeing Decimal
        .times(quantity) // Times the quantity.
        .plus(inclVat ? reduce(taxes, quantity) : 0) // Adding VAT times the quantity 
        .toDecimalPlaces(2) // Roundeing Decimal
        .toNumber(); // Returning the number.

    const getPriceWith = (prices: any, quantity: number, inclVat: boolean = settings.value.vat, country: string = settings.value.country, currency: string = settings.value.currency) => getPriceFor(prices[country].find((price: any) => price.currency === currency), quantity, inclVat);
    const getPriceWithX = (
        prices: any,
        quantity: number,
        {
            inclVat = settings.value.vat,
            country = settings.value.country,
            currency = settings.value.currency
        }: PriceOptions = {}
    ) => getPriceFor(prices[country].find((price: any) => price.currency === currency), quantity, inclVat);

    const getRefPriceFor = (price: any, quantity: number, inclVat: boolean = settings.value.vat) => calculate(price.salesPrice, price.salesTaxes, quantity, inclVat);

    const getPriceFor = (price: any, quantity: number, inclVat: boolean) => price.campaignPrice !== null
        ? calculate(price.campaignPrice, price.campaignTaxes, quantity, inclVat) // A campaign price exists.
        : calculate(price.salesPrice, price.salesTaxes, quantity, inclVat);

    const getDiscountPriceWith = (
        prices: any,
        quantity: number,
        {
            inclVat = settings.value.vat,
            country = settings.value.country,
            currency = settings.value.currency
        }: PriceOptions = {}
    ) => getDiscountPriceFor(prices[country].find((price: any) => price.currency === currency), quantity, inclVat);

    const getDiscountPriceFor = (price: any, quantity: number, inclVat: boolean) => price.discountPrice
        ? calculate(price.discountPrice, price.discountTaxes, quantity, inclVat)
        : 0;

    const getProductPreviousPrice = (product: any) => Object.keys(product.previousPrices30Days).length !== 0 && product.previousPrices30Days.constructor === Object
        ? getPriceFor(product.previousPrices30Days[settings.value.country]
            .filter((price: any) => price.currency === settings.value.currency)
            .find(() => true), product.quantity, settings.value.vat)
        : null;

    const sum = (sum1: number, sum2: number) => new Decimal(sum1)
        .toDecimalPlaces(2)
        .plus(new Decimal(sum2).toDecimalPlaces(2))
        .toDecimalPlaces(2)
        .toNumber();

    const sub = (value1: number, value2: number) => new Decimal(value1)
        .toDecimalPlaces(2)
        .sub(value2)
        .toDecimalPlaces(2)
        .toNumber();

    const times = (value: number, times: number) => new Decimal(value)
        .toDecimalPlaces(2)
        .times(times)
        .toDecimalPlaces(2)
        .toNumber();

    const div = (value: number, div: number) => new Decimal(value)
        .toDecimalPlaces(2)
        .div(div)
        .toDecimalPlaces(2)
        .toNumber();

    const display = (value: Number, currency?: String) => {
        return `${$i18n.n(value, 'currency', $i18n.locale, {
            currency: currency ?? settings.value.currency
        }).replace(currency ?? settings.value.currency, '').trim()} ${currency ?? settings.value.currency}`;
    };

    const getCurrentCurrency = () => {
        return settings.value.currency;
    }

    return {
        round,
        reduce,
        calculate,

        sum,
        sub,
        times,
        div,

        getPriceWith,
        getPriceWithX,
        getRefPriceFor,
        getPriceFor,
        getDiscountPriceWith,
        getDiscountPriceFor,
        getProductPreviousPrice,
        display,
        getCurrentCurrency
    };
};